import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { powerOfTwoValidator } from 'src/app/shared/validator/power-of-two.validator';
import {
  AcessosHorizontaisPorPortaPON, AttendanceSplitter, MitraNetworkParameters,
  SecoesDeSplitter, SequenciaDeCaixasNoBarramento, Splitter, UnbalancedBox
} from '../../state/network-parameters.model.generated';

@Component({
  selector: 'app-horizontal-unbalanced-form',
  templateUrl: './horizontal-unbalanced-form.component.html',
  styleUrls: ['./horizontal-unbalanced-form.component.scss']
})
export class HorizontalUnbalancedFormComponent implements OnInit {

  @Input()
  parameters: MitraNetworkParameters;

  @Input()
  parentForm: FormGroup;

  sections = ['', null, null];
  boxs = [];

  sectionsError = false;

  readonly allowedSplitterValues = ALLOWED_SPLITTER_VALUES;
  readonly allowedBoxValues = ALLOWED_BOX_VALUES;
  readonly unbalanced: Splitter = 2;
  readonly attendanceSplitter: AttendanceSplitter = 8;
  readonly lastBox: UnbalancedBox = '1/8';

  form: FormGroup;

  constructor(
    private formBuilder: FormBuilder
  ) { }

  ngOnInit(): void {

    // To avoid change after checked
    setTimeout(() => {
      this.createForm();
      this.loadSections();
      this.loadBoxes();
    }, 100);
  }

  sectionsChange(sections: string[]) {
    this.parameters.horizontal_settings.splitter_sections =
      sections
        .filter(s => s !== null && s !== '')
        .map(s => {
          return { splitter: Number(s.replace('1x', '')) };
        }) as SecoesDeSplitter;

    this.validateSections();
  }

  boxsChange(boxs: string[]) {
    const types = boxs.filter(s => s !== null && s !== '');
    this.parameters.horizontal_settings.bus_box_types = types as SequenciaDeCaixasNoBarramento;
    this.parameters.horizontal_settings.bus_box_types.push(this.lastBox);
    this.validateBoxes();
  }

  private createForm(): void {
    const { horizontal_access_per_pon, horizontal_settings } = this.parameters;
    const overbooking = horizontal_settings.overbooking ? horizontal_settings.overbooking * 100 : 0;

    let connector: string;

    if (horizontal_settings.fiber_connections === 'With PTO') {
      connector = horizontal_settings.connector_drop_pto;
    } else {
      connector = horizontal_settings.connector_customer_drop_nopto;
    }

    this.form = this.formBuilder.group({
      overbooking: [overbooking, [Validators.required, Validators.min(0), Validators.max(100)]],
      accessPerPon: [horizontal_access_per_pon, [Validators.required, Validators.min(1), Validators.max(128), powerOfTwoValidator()]],
      min_boxes_per_bus: [horizontal_settings.min_boxes_per_bus, [Validators.required, Validators.min(1), Validators.max(10)]],
      max_boxes_per_bus: [horizontal_settings.max_boxes_per_bus, [Validators.required, Validators.min(1), Validators.max(10)]],
      max_distance_between_boxes: [horizontal_settings.max_distance_between_boxes, [Validators.required, Validators.min(1)]],
      max_drop_length: [horizontal_settings.max_drop_length, [Validators.required, Validators.min(0)]],
      connector_box_drop: [horizontal_settings.connector_box_drop, Validators.required],
      fiber_connections: [horizontal_settings.fiber_connections, Validators.required],
      connector: [connector, Validators.required],
      boxArray: [true, Validators.requiredTrue]
    });
    this.parentForm.addControl('horizontal', this.form);

    this.parentForm.addControl('horizontalSections', this.formBuilder.control(true, Validators.requiredTrue));

    this.form.valueChanges.subscribe(v => {
      this.validateSections();
      this.updateParameters();
      this.updateBoxValue();
    });

    this.form.setValidators(MinMaxValidator);
    this.form.updateValueAndValidity();

  }

  private updateParameters() {
    const { value } = this.form;

    this.parameters.horizontal_access_per_pon = Number(value.accessPerPon) as AcessosHorizontaisPorPortaPON;
    this.parameters.horizontal_settings = {
      ...this.parameters.horizontal_settings,
      overbooking: value.overbooking ? Number(value.overbooking) / 100 : 0,
      min_boxes_per_bus: Number(value.min_boxes_per_bus),
      max_boxes_per_bus: Number(value.max_boxes_per_bus),
      attendance_splitter: Number(this.attendanceSplitter) as AttendanceSplitter,
      max_distance_between_boxes: Number(value.max_distance_between_boxes),
      max_drop_length: Number(value.max_drop_length),
      connector_box_drop: value.connector_box_drop,
      fiber_connections: value.fiber_connections
    };

    if (this.parameters.horizontal_settings.fiber_connections === 'With PTO') {
      this.parameters.horizontal_settings.connector_drop_pto = value.connector;
      this.parameters.horizontal_settings.connector_customer_drop_nopto = undefined;
    } else {
      this.parameters.horizontal_settings.connector_drop_pto = undefined;
      this.parameters.horizontal_settings.connector_customer_drop_nopto = value.connector;
    }
  }

  private loadSections() {
    if (!this.parameters.horizontal_settings.splitter_sections) {
      return;
    }
    this.parameters.horizontal_settings.splitter_sections
      .forEach((section, i) => {
        this.sections[i] = `1x${section.splitter}`;
      });
  }

  private validateSections(): void {
    const { splitter_sections } = this.parameters.horizontal_settings;

    let multiplication = 1;

    if (splitter_sections.length) {

      multiplication = splitter_sections.reduce((value, current) => {
        if (!ALLOWED_SPLITTER_VALUES.map(this.splitterToNumber).includes(current.splitter)) {
          return 0;
        }
        if (current.splitter) {
          return value * current.splitter;
        }
        return value;
      }, multiplication);
    }

    const { max_boxes_per_bus } = this.form.value;

    if (max_boxes_per_bus) {
      multiplication = multiplication * max_boxes_per_bus;
    }

    this.sectionsError = this.attendanceSplitter * multiplication !== Number(this.form.value.accessPerPon);

    this.parentForm.get('horizontalSections').setValue(!this.sectionsError);
  }

  private validateBoxes(): void {
    const busBoxTypes = this.parameters.horizontal_settings.bus_box_types;
    const boxError = this.form.value.max_boxes_per_bus !== busBoxTypes.filter(b => ALLOWED_BOX_VALUES.includes(b)).length;

    this.form.get('boxArray').setValue(!boxError);
  }

  private loadBoxes() {
    // if (this.parameters.horizontal_settings.bus_box_types.length !== 0) {
    //   this.boxs = this.parameters.horizontal_settings.bus_box_types;
    // }
    this.parameters.horizontal_settings.bus_box_types.forEach((box, i) => {
      if (i < this.parameters.horizontal_settings.bus_box_types.length - 1) {
        this.boxs[i] = box;
      }
    });
  }

  private updateBoxValue(): void {
    let max_boxes_per_bus = this.form.value.max_boxes_per_bus;

    if (max_boxes_per_bus === null) {
      return;
    }

    max_boxes_per_bus = max_boxes_per_bus - 1;

    if (max_boxes_per_bus === this.boxs.length || max_boxes_per_bus === null) {
      return;
    }

    if (max_boxes_per_bus < this.boxs.length) {
      const index = max_boxes_per_bus;
      const length = this.boxs.length - max_boxes_per_bus;
      this.boxs.splice(index, length);
    } else {
      const length = max_boxes_per_bus - this.boxs.length;
      const boxes = new Array(length).fill('');
      this.boxs = this.boxs.concat(boxes);

      if (this.boxs[0] === null) {
        this.boxs[0] = '';
      }
    }
    this.validateBoxes();
  }

  private splitterToNumber(splitter: string): number {
    const value = splitter.replace('1x', '');
    return Number(value);
  }
}


const MinMaxValidator: ValidatorFn = (fg: FormGroup) => {
  const min = fg.get('min_boxes_per_bus').value;
  const max = fg.get('max_boxes_per_bus').value;

  if (min === max) {
    return;
  }
  if (!min) {
    return null;
  }
  if (!max) {
    return null;
  }
  if (min < max) {
    return null;
  }
  return { minMax: true };
};

const ALLOWED_SPLITTER_VALUES = ['1x0', '1x2', '1x4', '1x8', '1x16', '1x32'];

const ALLOWED_BOX_VALUES = ['10/90', '15/85', '20/80', '25/75', '30/70', '35/65', '40/60', '1/8'];
